import { produce } from 'immer';
import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  setValidationErrors,
  showErrorToast,
  showSuccessToast,
} from '../../../../../../../common/global/helpers';
import useMount from '../../../../../../../common/hooks/useMount';
import Button from '../../../../../../../common/widgets/Button';
import Dropdown from '../../../../../../../common/widgets/Dropdown';
import Icons from '../../../../../../../common/widgets/Icons';
import Loader from '../../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../../common/widgets/LoadingSpinner';
import TextArea from '../../../../../../../common/widgets/TextArea';
import TimePicker from '../../../../../../../common/widgets/TimePicker';
import { CompanyAttendanceSettingsData } from '../../../../../models/CompanyAttendenceSettings';
import { ErrorState, ErrorType } from '../../../../../models/Errors';
import { resources } from '../../../../../resources/resources';
import { timesheetsAdminService } from '../../../../../services/timesheets-admin';
import { getDeadlineData } from '../../helper';

interface CompanySettingsFormState {
  isLoading: boolean;
  data: CompanyAttendanceSettingsData;
  selectedDeadline: { id: number; value: string };
  isSendingEmail: boolean;
  isSaving: boolean;
}
const initialData: CompanyAttendanceSettingsData = {
  startWork: '00:00',
  stopWork: '00:00',
  startBreak: '00:00',
  stopBreak: '00:00',
  emailBody: '',
  deadline: 0,
};

export default function CompanySettingsForm() {
  const { t: translate } = useTranslation();
  const [state, setState] = useState<CompanySettingsFormState>({
    isLoading: false,
    data: initialData,
    selectedDeadline: { id: 0, value: '' },
  } as CompanySettingsFormState);
  const [errors, setErrors] = useState<ErrorState>({});

  const timeStringToDate = (time: string | null): Date | null => {
    if (!time) return null;
    const momentTime = moment(
      time,
      ['HH:mm:ss', 'HH:mm', 'h:mm:ss A', 'h:mm A'],
      true,
    );
    return momentTime.isValid() ? momentTime.toDate() : null;
  };

  const dateToTimeString = (date: Date | null): string => {
    if (!date) return '00:00';
    return moment(date).format('HH:mm');
  };

  const loadData = async (abortController?: AbortController) => {
    setState(
      produce(d => {
        d.isLoading = true;
      }),
    );
    const response = await timesheetsAdminService.getCompanySettings({
      signal: abortController?.signal,
    });
    if (!response.hasErrors) {
      const parseTime = (value: any): string => {
        if (!value) return '00:00';
        const momentTime = moment(
          value,
          ['HH:mm:ss', 'HH:mm', 'h:mm:ss A', 'h:mm A'],
          true,
        );
        return momentTime.isValid() ? momentTime.format('HH:mm:ss') : '00:00';
      };
      setState(
        produce(d => {
          d.data.startWork = parseTime(response.data.startWork);
          d.data.stopWork = parseTime(response.data.stopWork);
          d.data.startBreak = parseTime(response.data.startBreak);
          d.data.stopBreak = parseTime(response.data.stopBreak);
          d.data.emailBody = response.data.emailBody;
          d.data.deadline = response.data.deadline;
          d.selectedDeadline = {
            id: response.data.deadline,
            value: response.data.deadline.toString(),
          };
        }),
      );
    }
    setState(
      produce(d => {
        d.isLoading = false;
      }),
    );
  };

  const handleSendTestEmail = async () => {
    setState(
      produce(d => {
        d.isSendingEmail = true;
      }),
    );
    const response = await timesheetsAdminService.sendTestEmail(
      state.data.emailBody,
    );
    if (!response.hasErrors) {
      showSuccessToast(
        translate(resources.admin.timesheetsSettings.company.emailSendSuccess),
      );
    } else {
      showErrorToast(
        translate(resources.admin.timesheetsSettings.company.emailSendError),
      );
    }
    setState(
      produce(d => {
        d.isSendingEmail = false;
      }),
    );
  };

  const handleSaveChanges = async () => {
    setState(
      produce(d => {
        d.isSaving = true;
      }),
    );
    const payload = {
      ...state.data,
      startWork: state.data.startWork || '00:00',
      stopWork: state.data.stopWork || '00:00',
      startBreak: state.data.startBreak || '00:00',
      stopBreak: state.data.stopBreak || '00:00',
    };
    const response =
      await timesheetsAdminService.saveCompanyAttendenceSettings(payload);
    if (!response.hasErrors) {
      showSuccessToast(
        translate(
          resources.admin.timesheetsSettings.company.changesSavedSuccess,
        ),
      );
    } else if (response.errorType === ErrorType.ValidationError) {
      setValidationErrors(response, setErrors);
    } else {
      showErrorToast(
        translate(resources.admin.timesheetsSettings.company.changesSavedError),
      );
    }
    setState(
      produce(d => {
        d.isSaving = false;
      }),
    );
  };

  const clearErrors = (name: string) => {
    setErrors(
      produce(draft => {
        if (draft) {
          draft[name] = [];
        }
      }),
    );
  };

  useMount(loadData);

  return (
    <div className="panel padding-xl timesheet-settings-company">
      <div className="page-actions">
        <h1>{translate(resources.admin.timesheetsSettings.company.title)}</h1>
        <div className="page-action admin-actions">
          <Button
            className="btn btn-solid btn-sm"
            onClick={handleSaveChanges}
            loading={state.isSaving}
          >
            <span className="btn-text">
              {translate(resources.admin.timesheetsSettings.company.action)}
            </span>
          </Button>
        </div>
      </div>
      <Loader loading={state.isLoading} loader={<LoadingSpinner />}>
        <div className="tsc-form">
          <div className="mb-10">
            <h4 className="timesheet-settings-subtitle">
              {translate(
                resources.admin.timesheetsSettings.company.timeIntervals,
              )}
            </h4>

            <div className="tsc-time-intervals">
              <div className="tsc-time-intervals-row">
                <label className="tsc-inline-label">
                  {translate(
                    resources.admin.timesheetsSettings.company.workSchedule,
                  )}
                </label>
                <TimePicker
                  value={timeStringToDate(state.data.startWork)}
                  onChange={e => {
                    setState(
                      produce(d => {
                        d.data.startWork = dateToTimeString(e.value);
                      }),
                    );
                  }}
                  errors={errors.startWork}
                  clearErrors={() => clearErrors('startWork')}
                  popupSettings={{
                    appendTo:
                      (document.getElementsByClassName(
                        'k-dialog',
                      )[0] as HTMLElement) ?? undefined,
                  }}
                />
                <span className="tsc-time-intervals-icon">
                  <Icons.ArrowUp2 />
                </span>

                <TimePicker
                  value={timeStringToDate(state.data.stopWork)}
                  onChange={e => {
                    setState(
                      produce(d => {
                        d.data.stopWork = dateToTimeString(e.value);
                      }),
                    );
                  }}
                  errors={errors.stopWork}
                  clearErrors={() => clearErrors('stopWork')}
                  popupSettings={{
                    appendTo:
                      (document.getElementsByClassName(
                        'k-dialog',
                      )[0] as HTMLElement) ?? undefined,
                  }}
                />
              </div>
              <div className="tsc-time-intervals-row">
                <label className="tsc-inline-label">
                  {translate(resources.admin.timesheetsSettings.company.break)}
                </label>
                <TimePicker
                  value={timeStringToDate(state.data.startBreak)}
                  onChange={e => {
                    setState(
                      produce(d => {
                        d.data.startBreak = dateToTimeString(e.value);
                      }),
                    );
                  }}
                  errors={errors.startBreak}
                  clearErrors={() => clearErrors('startBreak')}
                  popupSettings={{
                    appendTo:
                      (document.getElementsByClassName(
                        'k-dialog',
                      )[0] as HTMLElement) ?? undefined,
                  }}
                />
                <span className="tsc-time-intervals-icon">
                  <Icons.ArrowUp2 />
                </span>

                <TimePicker
                  value={timeStringToDate(state.data.stopBreak)}
                  onChange={e => {
                    setState(
                      produce(d => {
                        d.data.stopBreak = dateToTimeString(e.value);
                      }),
                    );
                  }}
                  errors={errors.stopBreak}
                  clearErrors={() => clearErrors('stopBreak')}
                  popupSettings={{
                    appendTo:
                      (document.getElementsByClassName(
                        'k-dialog',
                      )[0] as HTMLElement) ?? undefined,
                  }}
                />
              </div>
            </div>
          </div>

          <div className="mb-30">
            <h4 className="timesheet-settings-subtitle">
              {translate(resources.admin.timesheetsSettings.company.emailBody)}
            </h4>
            <TextArea
              value={state.data.emailBody}
              rows={5}
              onChange={e => {
                setState(
                  produce(d => {
                    d.data.emailBody = e.value;
                  }),
                );
              }}
              errors={errors.emailBody}
              clearErrors={() => clearErrors('emailBody')}
            />

            <Button
              className="btn btn-outline btn-sm"
              onClick={handleSendTestEmail}
              loading={state.isSendingEmail}
            >
              <span className="btn-icon">
                <Icons.ArrowButton />
              </span>
              <span className="btn-text">
                {translate(
                  resources.admin.timesheetsSettings.company.emailTest,
                )}
              </span>
            </Button>
          </div>

          <div>
            <h4 className="timesheet-settings-subtitle">
              {translate(
                resources.admin.timesheetsSettings.company.dataReminder,
              )}
            </h4>

            <Dropdown
              data={getDeadlineData()}
              dataItemKey="id"
              textField="value"
              value={state.selectedDeadline}
              onChange={e => {
                setState(
                  produce(d => {
                    d.selectedDeadline = e.value;
                    d.data.deadline = e.value.id;
                  }),
                );
              }}
              errors={errors.deadline}
              clearErrors={() => clearErrors('deadline')}
            />
          </div>
        </div>
      </Loader>
    </div>
  );
}
